import { Injectable } from '@angular/core';
import { filter, pairwise, startWith, Subscription } from 'rxjs';
import { modifiedThrottleTime } from 'src/app/utilities/ZoneUtils';
import { SessionsVptService } from 'src/app/services/sessions-vpt.service';
import { UserEventType } from 'src/app/models/session-sync';
import {
  IExpandAndFullScreenChange,
  ISessionViewState,
  SessionSharedDataService,
  SessionView,
} from 'src/app/services/session-shared-data.service';
import { RealtimeSpaceService } from 'src/app/services/realtime-space.service';
import { fabric } from 'fabric';
import { Location } from 'src/app/state/temporary-user-metadata-repository.service';
import { SpaceBoardsService } from 'src/app/services/space-boards.service';
import { SpaceBoardFoldersService } from 'src/app/services/space-board-folders.service';
import { SpaceRepository } from 'src/app/state/space.repository';
import { InspectModeRepository } from 'src/app/state/inspect-mode-repository.service';
import { isFullScreeningAppInCall } from 'src/app/sessions/session/services/full-screen-app-manager.service';
import { ForcingMinimumZoomService } from '../forcing-minimum-zoom.service';

export interface SendingAndListeningForEventsOpts {
  stateIsNotChanged: boolean;
  stopSendingVptEvents: boolean;
  startSendingVptEvents: boolean;
  startListeningForVptEvents: boolean;
  stopListeningForVptEvents: boolean;
  handleVptLock: boolean;
  followingUserID: string;
}

interface ILocalFollowingSessionViewState {
  view: SessionView | undefined;
  sessionViewMetaData: any;
}

@Injectable({
  providedIn: 'root',
})
export class LeaderFollowModesSharedUtilsService {
  private vptSubscription?: Subscription;
  private leaderExpandStateSubscription?: Subscription;

  constructor(
    private sessionsVptService: SessionsVptService,
    private sharedDataService: SessionSharedDataService,
    private realtimeSpaceService: RealtimeSpaceService,
    private spaceBoardsService: SpaceBoardsService,
    private spaceBoardFolderService: SpaceBoardFoldersService,
    private spaceRepo: SpaceRepository,
    private forcingMinimumZoomService: ForcingMinimumZoomService,
    private inspectModeRepository: InspectModeRepository,
  ) {}

  private startSendingVPTEvents() {
    this.vptSubscription?.unsubscribe();

    this.vptSubscription = this.sessionsVptService.viewportTransform
      .pipe(modifiedThrottleTime(1000 / 30, true))
      .subscribe((vpt) => {
        const frameUID = this.sharedDataService.getCurrentFrame()?.uid;
        if (!vpt || !frameUID) {
          return;
        }

        const fabricCanvas = this.sharedDataService.fabricCanvas;
        const event = {
          frameUID,
          type: UserEventType.LOCATION,
          location: {
            vpt,
            canvasSize: this.sessionsVptService.getCanvasSize(fabricCanvas),
            frameUID,
          },
        };
        this.realtimeSpaceService.service.sendUserEvent(event);
      });
  }

  private startSendingSessionViewAndExpandEvent() {
    const dummySessionViewState: ISessionViewState = {
      view: SessionView.LOADING,
    };
    this.leaderExpandStateSubscription?.unsubscribe();
    this.leaderExpandStateSubscription = this.sharedDataService.sessionView.current$
      .pipe(
        filter(
          (sessionViewState) => !sessionViewState.sessionViewMetaData?.fullScreeningAPrivateApp,
        ),
        startWith(dummySessionViewState),
        pairwise(),
      )
      .subscribe(([prevSessionViewState, currSessionViewState]) => {
        if (!currSessionViewState.expandedFullScreenChange) {
          this.realtimeSpaceService.service.modifyTemporaryUserMetadata({
            sessionView: currSessionViewState.view,
            expandAndFullScreenChange: undefined,
            sessionViewMetaData: currSessionViewState.sessionViewMetaData,
          });
          return;
        }
        if (
          currSessionViewState.expandedFullScreenChange?.tileData &&
          prevSessionViewState.expandedFullScreenChange?.id !==
            currSessionViewState.expandedFullScreenChange?.id
        ) {
          this.realtimeSpaceService.service.modifyTemporaryUserMetadata({
            sessionView: currSessionViewState.view,
            expandAndFullScreenChange: currSessionViewState.expandedFullScreenChange,
            sessionViewMetaData: currSessionViewState.sessionViewMetaData,
          });
        }
      });
  }

  private stopSendingVPTEvents() {
    this.vptSubscription?.unsubscribe();
  }

  private stopSendingSessionViewAndExpandEvents() {
    this.leaderExpandStateSubscription?.unsubscribe();
  }

  // Join the room of the leader / following to get the vpt updates
  private joinFollowingUserRoom(userId: string) {
    this.realtimeSpaceService.service.joinUserRoom(userId);
  }

  private leaveFollowingUserRoom(userId: string) {
    this.realtimeSpaceService.service.leaveUserRoom(userId);
  }

  public handleSendingAndListeningForEvents(
    sendingAndListeningForEventsOpts: SendingAndListeningForEventsOpts,
  ) {
    const {
      stateIsNotChanged,
      stopSendingVptEvents,
      startSendingVptEvents,
      startListeningForVptEvents,
      stopListeningForVptEvents,
      handleVptLock,
      followingUserID,
    } = sendingAndListeningForEventsOpts;

    if (stateIsNotChanged) {
      return;
    }

    if (stopSendingVptEvents) {
      this.stopSendingVPTEvents();
      this.stopSendingSessionViewAndExpandEvents();
    }

    if (startSendingVptEvents) {
      this.startSendingVPTEvents();
      this.startSendingSessionViewAndExpandEvent();
    }

    if (startListeningForVptEvents) {
      this.joinFollowingUserRoom(followingUserID);
    }

    if (stopListeningForVptEvents) {
      this.leaveFollowingUserRoom(followingUserID);
    }

    if (handleVptLock) {
      if (startListeningForVptEvents) {
        this.sharedDataService.vptLocked.next(true);
      } else if (startSendingVptEvents || stopListeningForVptEvents) {
        this.sharedDataService.vptLocked.next(false);
      }
    }
  }

  public selectFollowingSessionView(
    leaderSessionView: SessionView | undefined,
    expandAndFullScreenChange: IExpandAndFullScreenChange | undefined,
    sessionViewMetaData:
      | {
          fullscreenId: string;
          fullScreeningAPrivateApp: boolean;
        }
      | undefined,
  ) {
    const updatedFollowingSessionViewState = this.getOverriddenFollowingSessionViewState(
      leaderSessionView,
      sessionViewMetaData,
    );
    const updatedFollowingSessionView = updatedFollowingSessionViewState.view;
    const updatedFollowingSessionViewMetaData =
      updatedFollowingSessionViewState.sessionViewMetaData;
    if (
      expandAndFullScreenChange &&
      updatedFollowingSessionView &&
      [SessionView.EXPANDED, SessionView.FULLSCREEN].includes(updatedFollowingSessionView)
    ) {
      this.sharedDataService.sessionView.switchToSessionView(
        updatedFollowingSessionView,
        expandAndFullScreenChange,
        undefined,
        updatedFollowingSessionViewMetaData,
      );
      return;
    }

    if (updatedFollowingSessionView) {
      this.sharedDataService.sessionView.switchToSessionView(
        updatedFollowingSessionView,
        undefined,
        undefined,
        updatedFollowingSessionViewMetaData,
      );
    }

    if (!expandAndFullScreenChange) {
      this.sharedDataService.sessionView.updateExpandedFullScreen(
        null,
        updatedFollowingSessionViewMetaData,
      );
    }
  }

  public updateVptUsingFollowingLocation(location: Location, applyCentering: boolean) {
    const canvas = this.sharedDataService.fabricCanvas;
    if (!canvas) {
      return;
    }

    const canvasSize = canvas.getCanvasSize();
    if (!canvasSize[0] || !canvasSize[1] || !location.canvasSize[0] || !location.canvasSize[1]) {
      return;
    }

    // discard selected objects
    if (canvas.getActiveObjects().length > 0) {
      canvas.discardActiveObject();
    }

    const decomposed = { ...fabric.util.qrDecompose(location.vpt), flipX: false, flipY: false };

    const widthFactor = canvasSize[0] / location.canvasSize[0];
    const heightFactor = canvasSize[1] / location.canvasSize[1];

    const factor = Math.min(widthFactor, heightFactor);

    decomposed.scaleX *= factor;
    decomposed.scaleY *= factor;

    decomposed.translateX *= factor;
    decomposed.translateY *= factor;

    const vpt = fabric.util.composeMatrix(decomposed);

    // calculation is done on minimum factor
    // we have to cater for the difference between minimum factor and width/height factor
    // to get the correct center coords
    const centerX = (location.canvasSize[0] * widthFactor - location.canvasSize[0] * factor) / 2;
    const centerY = (location.canvasSize[1] * heightFactor - location.canvasSize[1] * factor) / 2;

    if (applyCentering) {
      vpt[5] = vpt[5] + centerY;
      vpt[4] = vpt[4] + centerX;
    }

    const updatedVpt = this.forcingMinimumZoomService.handleForceMinimumTouchZoom(
      vpt,
      location,
      canvas.getCanvasSize(),
    );
    this.sessionsVptService.setVPT(updatedVpt, true);
  }

  public selectFollowingFrame(followingFrameUid: string) {
    const allFrames = this.spaceBoardsService.activeSpaceCurrentRoomFrames;

    const selectedFrame = allFrames.find((s) => s.uid === followingFrameUid);

    // prevent calling the subject with the same uid again
    if (followingFrameUid === this.spaceRepo.activeSpaceSelectedBoardUid || !followingFrameUid) {
      /**
       * we need to achieve the following of the frame folder isn't the active one,
       * and user moved the frame to another folder, we need to activate the frame new folder
       */
      if (selectedFrame) {
        this.spaceBoardFolderService.updateActiveBoardFolderForSelectedFrame(selectedFrame);
      }
      return;
    }
    const accessibleFrames = this.spaceBoardsService.getActiveSpaceCurrentRoomAccessibleBoards();
    if (
      !selectedFrame // frame is not in space, do nothing
    ) {
      return;
    }
    // check if the new leader frame exists in the user frames
    const isFollowingFrameExist = accessibleFrames.find((frame) => frame.uid === followingFrameUid);

    if (!isFollowingFrameExist) {
      this.sharedDataService.selectFrameSubject.next(undefined);
      return;
    }

    this.sharedDataService.selectFrameSubject.next(followingFrameUid);
  }

  public handleDetectJoinedOrLeftCall(
    isCallConnected: boolean,
    followingSessionView: SessionView | undefined,
    expandAndFullScreenChange: IExpandAndFullScreenChange | undefined,
    sessionViewMetaData:
      | {
          fullscreenId: string;
          fullScreeningAPrivateApp: boolean;
        }
      | undefined,
    currentUserIsBeingFollowed: boolean,
  ): void {
    const updatedFollowingSessionViewState = this.getOverriddenFollowingSessionViewState(
      followingSessionView,
      sessionViewMetaData,
    );
    const updatedFollowingSessionView = updatedFollowingSessionViewState.view;
    const updatedFollowingSessionViewMetaData =
      updatedFollowingSessionViewState.sessionViewMetaData;

    if (updatedFollowingSessionView) {
      this.sharedDataService.sessionView.switchToSessionView(
        updatedFollowingSessionView,
        expandAndFullScreenChange,
        undefined,
        updatedFollowingSessionViewMetaData,
      );
    }

    if (isCallConnected) {
      setTimeout(() => {
        if (expandAndFullScreenChange) {
          this.sharedDataService.sessionView.switchToSessionView(
            expandAndFullScreenChange.fullscreen ? SessionView.FULLSCREEN : SessionView.EXPANDED,
            expandAndFullScreenChange,
          );
        }
      }, 2000);
    } else {
      // set expand fullscreen to null when user leaves call and is leader
      // this is a custom solution for [SPAC-3306]
      // it aims to avoid a bug where the leader is on a view (whiteboard) other than the participant's view (gallery) after leaving and rejoining the call
      if (
        currentUserIsBeingFollowed &&
        this.sharedDataService.sessionView.getSessionView() !== SessionView.FULLSCREEN_APP
      ) {
        this.sharedDataService.sessionView.updateExpandedFullScreen(null);
      }
    }
  }

  /**
   * In case of inspect mode and the user being followed is in a full screen app during call,
   * we override the following session view to FULLSCREEN_APP so the app keeps being shown
   * in whiteboard in full screen, because the user in inspect mode cannot see the GV or
   * the expanded tile with fullscreened app.
   */
  public getOverriddenFollowingSessionViewState(
    followingSessionView: SessionView | undefined,
    sessionViewMetaData: any,
  ): ILocalFollowingSessionViewState {
    if (
      this.inspectModeRepository.isInspectMode &&
      followingSessionView &&
      isFullScreeningAppInCall({ view: followingSessionView, sessionViewMetaData })
    ) {
      return {
        view: SessionView.FULLSCREEN_APP,
        sessionViewMetaData: {
          ...sessionViewMetaData,
          fullScreeningAppInCall: false,
        },
      };
    }

    return { view: followingSessionView, sessionViewMetaData };
  }

  public updatePersistentLocation(frameUID?: string) {
    // Update the persistent location so there is an initial value for sure
    if (this.sharedDataService.fabricCanvas?.viewportTransform) {
      if (frameUID) {
        this.sessionsVptService.updatePersistentLocation(
          this.sharedDataService.fabricCanvas?.viewportTransform,
          frameUID,
        );
      }
    }
  }

  public cleanUpService() {
    this.stopSendingVPTEvents();
    this.stopSendingSessionViewAndExpandEvents();
    this.forcingMinimumZoomService.cleanUp();
  }
}
